import type {
	CampaignCardView,
	CampaignPlacement,
} from "../../../../shared/types/campaign-card.type";
import { StatusChip } from "../../../../shared/components/status-chip/StatusChip";
import {
	fmtDec,
	fmtMoney,
	fmtNum,
	placementColor,
} from "../../../../shared/utils/campaign-metrics-format";
import { CreativesTable } from "./CreativesTable";
import { getPlacementShares } from "./placement-share";
import { PlacementMetricsTooltip } from "./PlacementMetricsTooltip";

type Props = {
	campaign: CampaignCardView;
};

export const StructureFlow = ({ campaign }: Props) => {
	const totalCost = campaign.metrics.cost || 1;

	return (
		<div className='campaign-detail__structure-grid'>
			<div className='campaign-detail__panel campaign-detail__panel--allow-overflow'>
				<div className='campaign-detail__panel-head'>GEO Flow</div>
				<div className='campaign-detail__panel-body campaign-detail__flow-body'>
					{campaign.geo.map((g) => {
						const pct = (g.metrics.cost / totalCost) * 100;
						return (
							<div key={g.country} className='campaign-detail__flow-card'>
								<div className='campaign-detail__flow-card-head'>
									<span className='campaign-detail__flow-card-title'>
										{g.country}
									</span>
									<span className='campaign-detail__flow-card-meta'>
										{fmtMoney(g.metrics.cost)} · {fmtDec(pct, 1)}%
									</span>
								</div>
								<PlacementFlowBar
									placements={g.placements}
									costOnly
								/>
								<PlacementFlowTags
									placements={g.placements}
									context={g.country}
									costOnly
								/>
							</div>
						);
					})}
				</div>
			</div>

			<div className='campaign-detail__panel campaign-detail__panel--allow-overflow'>
				<div className='campaign-detail__panel-head'>Ad Group Flow</div>
				<div className='campaign-detail__panel-body campaign-detail__flow-body'>
					{campaign.adgroups.map((ag) => {
						const pct = (ag.metrics.cost / totalCost) * 100;
						return (
							<div key={ag.id} className='campaign-detail__flow-card'>
								<div className='campaign-detail__flow-card-head'>
									<span className='campaign-detail__flow-card-title'>
										{ag.name}
									</span>
									<StatusChip status={ag.status} />
									<span className='campaign-detail__flow-card-meta'>
										{fmtMoney(ag.metrics.cost)} · {fmtDec(pct, 1)}% · Impr{" "}
										{fmtNum(ag.metrics.impr)}
									</span>
								</div>
								<PlacementFlowBar
									placements={ag.placements}
									costOnly
								/>
								<PlacementFlowTags
									placements={ag.placements}
									context={ag.name}
									costOnly
								/>
								<CreativesTable creatives={ag.creatives} />
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
};

function PlacementFlowBar({
	placements,
	costOnly,
}: {
	placements: CampaignPlacement[];
	costOnly?: boolean;
}) {
	if (!placements.length) return null;

	const shares = getPlacementShares(placements, { costOnly });

	const segmentStyle = (sharePct: number) => ({
		width: `${sharePct}%`,
		flexShrink: 0,
		minWidth: 0,
	});

	return (
		<div className='campaign-detail__flow-bar-host'>
			<div className='campaign-detail__flow-bar-track'>
				{shares.map(({ placement: p, sharePct }, index) => {
					const isFirst = index === 0;
					const isLast = index === shares.length - 1;
					return (
						<div
							key={p.name}
							className='campaign-detail__flow-track-cell'
							style={segmentStyle(sharePct)}>
							<div
								className={`campaign-detail__flow-bar-segment${isFirst ? " campaign-detail__flow-bar-segment--first" : ""}${isLast ? " campaign-detail__flow-bar-segment--last" : ""}`}
								style={{
									background: placementColor(p.name),
								}}
							/>
						</div>
					);
				})}
			</div>
		</div>
	);
}

function PlacementFlowTags({
	placements,
	context,
	costOnly,
}: {
	placements: CampaignPlacement[];
	context: string;
	costOnly?: boolean;
}) {
	if (!placements.length) return null;

	return (
		<div className='campaign-detail__flow-tags'>
			{getPlacementShares(placements, { costOnly }).map(
				({ placement: p, sharePct }) => (
					<span key={p.name} className='campaign-detail__flow-tag'>
						<span
							className='campaign-detail__flow-dot'
							style={{
								background: placementColor(p.name),
							}}
						/>
						{p.name}
						<PlacementMetricsTooltip
							context={context}
							placementName={p.name}
							metrics={p.metrics}
							sharePct={sharePct}
						/>
					</span>
				),
			)}
		</div>
	);
}
